import { inject, Injectable } from '@angular/core';
import { AssetsStore } from '../../../stores/assets.store';
import { AssetResponse, getAssetScores, getAssetV2, getPublicAssetV2 } from '..';
import { SYSTEM_ORDER } from '../fundamental-scores';
import { AuthStore } from '../../../stores/auth.store';
import { AuthBridgeService } from '../../auth-bridge';

@Injectable({ providedIn: 'root' })
export class WorkflowAssetService {
  private readonly store = inject(AssetsStore);
  private readonly authStore = inject(AuthStore);
  private readonly authBridge = inject(AuthBridgeService);

  load(isin: string) {
    this.store.startLoading(isin);

    const isGuest =
      this.authBridge.authResolved() && !this.authBridge.token() && !this.authBridge.tokenError();
    const dataPromise = isGuest
      ? getPublicAssetV2(isin)
      : getAssetV2(isin, this.authBridge, this.authStore);

    // Guard prevents a stale response (for an ISIN the user has already navigated
    // away from) from overwriting state for the ISIN currently being viewed — see the
    // same guard in loadFundamentalScores.
    return dataPromise
      .then((data) => {
        if (this.store.isin() !== isin) return;
        if (!data) {
          this.store.setError('No data returned');
          return;
        }
        this.store.setV2Data(data as AssetResponse);
      })
      .catch((err) => {
        if (this.store.isin() === isin) this.store.setError(String(err));
      });
  }

  loadFundamentalScores(isin: string) {
    return getAssetScores(isin, SYSTEM_ORDER, this.authBridge, this.authStore)
      .then((data) => {
        // Guard against a stale response landing after the user has already navigated to a
        // different ISIN — an in-flight request for the old ISIN must not overwrite the
        // store with data for a stock that's no longer being viewed.
        if (this.store.isin() === isin) this.store.setFundamentalScores(data);
      })
      .catch(() => {
        if (this.store.isin() === isin) this.store.setFundamentalScores(null);
      });
  }
}
